/** Forum owns the opt-out controls; Console reads the shared preference. */
const COOKIE_NAME = 'metrly_measurement'
export const MEASUREMENT_CONSENT_VERSION = '2026-10-21'
const MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000
const SUPPORTED_VERSIONS = new Set(['2026-10-15', MEASUREMENT_CONSENT_VERSION])

type Choice = { version: string; decision: 'granted' | 'denied'; at: number }
export type MeasurementPreference = {
  version: string
  decision: 'granted' | 'denied' | 'default'
  source: 'measurement_preferences' | 'opt_out_default' | 'global_privacy_control'
}
type Gtag = (...args: unknown[]) => void
let revokedGtag: Gtag | null = null

export function readMeasurementConsent(): MeasurementPreference | null {
  if (typeof navigator !== 'undefined'
      && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) {
    return { version: MEASUREMENT_CONSENT_VERSION, decision: 'denied', source: 'global_privacy_control' }
  }
  if (typeof document === 'undefined') return null
  try {
    const matches = document.cookie.split(';').map(part => part.trim())
      .filter(part => part.startsWith(`${COOKIE_NAME}=`))
    if (!matches.length) {
      return { version: MEASUREMENT_CONSENT_VERSION, decision: 'default', source: 'opt_out_default' }
    }
    if (matches.length !== 1) return null
    const raw = matches[0].slice(COOKIE_NAME.length + 1)
    if (!raw) return null
    const value: unknown = JSON.parse(decodeURIComponent(raw))
    if (!value || typeof value !== 'object') return null
    const choice = value as Partial<Choice>
    const now = Date.now()
    if (!SUPPORTED_VERSIONS.has(choice.version || '') || (choice.decision !== 'granted' && choice.decision !== 'denied')
        || typeof choice.at !== 'number' || !Number.isSafeInteger(choice.at)
        || choice.at > now || (choice.decision === 'granted' && now - choice.at > MAX_AGE_MS)) return null
    return { version: choice.version!, decision: choice.decision, source: 'measurement_preferences' }
  } catch {
    return null
  }
}

/** Missing preferences enable measurement; saved opt-outs and GPC override it. */
export function hasMeasurementConsent(): boolean {
  const choice = readMeasurementConsent()
  const granted = choice?.decision === 'granted' || choice?.decision === 'default'
  if (typeof window === 'undefined') return granted
  const gtag = (window as Window & { gtag?: Gtag }).gtag
  if (!granted && gtag && revokedGtag !== gtag) {
    gtag('consent', 'update', {
      ad_storage: 'denied', analytics_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied',
    })
    revokedGtag = gtag
  } else if (granted && gtag && revokedGtag === gtag) {
    gtag('consent', 'update', {
      ad_storage: 'granted', analytics_storage: 'granted',
      ad_user_data: 'granted', ad_personalization: 'denied',
    })
    revokedGtag = null
  }
  return granted
}

export function __resetMeasurementConsentForTests(): void {
  revokedGtag = null
}
